import React, { ReactElement, useMemo, useState } from "react";
import { StatsService } from "../../stats";
import { NumericFormat } from "react-number-format";

const GOAL = 160000;

const getProgressColor = (percent: number): string => {
	if (percent >= 100) return "#10b981"; // Green - Goal reached!
	if (percent >= 75) return "#22c55e";  // Light green - Almost there
	if (percent >= 50) return "#eab308";  // Yellow - Halfway
	if (percent >= 25) return "#f97316";  // Orange - Getting started
	return "#6366f1"; // Purple - Beginning
};

const getEncouragingMessage = (percent: number): { message: string; emoji: string } => {
	if (percent >= 100) return { message: "🎉 GOAL CRUSHED!", emoji: "🏆" };
	if (percent >= 90) return { message: "So close! Push to the finish!", emoji: "🔥" };
	if (percent >= 75) return { message: "Incredible progress!", emoji: "⚡" };
	if (percent >= 50) return { message: "Halfway there! Keep going!", emoji: "💪" };
	if (percent >= 25) return { message: "Great momentum building!", emoji: "🚀" };
	if (percent >= 10) return { message: "Off to a solid start!", emoji: "✨" };
	return { message: "Let's make it happen!", emoji: "🎯" };
};

const milestones = [
	{ percent: 25, label: "25%", icon: "🌟" },
	{ percent: 50, label: "50%", icon: "⭐" },
	{ percent: 75, label: "75%", icon: "🌟" },
	{ percent: 100, label: "100%", icon: "🏆" },
];

export const SalesThisMonthGauge = (props: any): ReactElement => {
	const [sales, setSales] = useState<number>(0);
	const statsService = new StatsService();

	useMemo(() => {
		const fetchSales = async () => {
			const salesStats = await statsService.findSalesStats(props.date);
			setSales(Number(salesStats.currentMonth) || 0);
		};

		fetchSales();
	}, [props.date]);

	// Ensure sales is always a number
	const salesAmount = Number(sales) || 0;
	const percent = Math.min((salesAmount / GOAL) * 100, 100);
	const progressColor = getProgressColor(percent);
	const { message, emoji } = getEncouragingMessage(percent);
	const isGoalMet = percent >= 100;
	const remaining = GOAL - salesAmount;

	// Calculate the circle progress
	const radius = 90;
	const circumference = 2 * Math.PI * radius;
	const strokeDashoffset = circumference - (percent / 100) * circumference;

	return (
		<div className={`sales-goal-container ${isGoalMet ? 'goal-achieved' : ''}`}>
			{/* Celebration particles when goal is met */}
			{isGoalMet && (
				<div className="celebration-particles">
					{[...Array(12)].map((_, i) => (
						<span key={i} className="particle" style={{ '--i': i } as React.CSSProperties}>✨</span>
					))}
				</div>
			)}

			{/* Progress Ring */}
			<div className="progress-ring-container">
				<svg className="progress-ring" width="220" height="220" viewBox="0 0 220 220">
					{/* Background track */}
					<circle
						className="progress-ring-track"
						cx="110"
						cy="110"
						r={radius}
						fill="none"
						strokeWidth="12"
					/>
					{/* Progress arc */}
					<circle
						className="progress-ring-progress"
						cx="110"
						cy="110"
						r={radius}
						fill="none"
						strokeWidth="12"
						strokeLinecap="round"
						style={{
							stroke: progressColor,
							strokeDasharray: circumference,
							strokeDashoffset: strokeDashoffset,
						}}
					/>
					{/* Milestone markers */}
					{milestones.map((milestone) => {
						const angle = (milestone.percent / 100) * 360 - 90;
						const achieved = percent >= milestone.percent;
						return (
							<g key={milestone.percent}>
								<circle
									cx={110 + radius * Math.cos((angle * Math.PI) / 180)}
									cy={110 + radius * Math.sin((angle * Math.PI) / 180)}
									r="6"
									fill={achieved ? progressColor : "#e2e8f0"}
									className={achieved ? 'milestone-achieved' : ''}
								/>
							</g>
						);
					})}
				</svg>

				{/* Center content */}
				<div className="progress-ring-center">
					{isGoalMet ? (
						<div className="trophy-icon">🏆</div>
					) : (
						<span className="progress-emoji">{emoji}</span>
					)}
					<div className="sales-amount">
						<NumericFormat
							value={salesAmount.toFixed(0)}
							displayType="text"
							thousandSeparator={true}
							prefix="$"
						/>
					</div>
					<div className="progress-percent" style={{ color: progressColor }}>
						{percent.toFixed(0)}%
					</div>
				</div>
			</div>

			{/* Message */}
			<div className="goal-message" style={{ color: progressColor }}>
				{message}
			</div>

			{/* Goal info */}
			<div className="goal-info">
				{isGoalMet ? (
					<span className="goal-exceeded">
						Exceeded by <NumericFormat value={(salesAmount - GOAL).toFixed(0)} displayType="text" thousandSeparator={true} prefix="$" />!
					</span>
				) : (
					<span className="goal-remaining">
						<NumericFormat value={remaining.toFixed(0)} displayType="text" thousandSeparator={true} prefix="$" /> to go
					</span>
				)}
			</div>

			{/* Milestone badges */}
			<div className="milestone-badges">
				{milestones.map((milestone) => {
					const achieved = percent >= milestone.percent;
					return (
						<div
							key={milestone.percent}
							className={`milestone-badge ${achieved ? 'achieved' : ''}`}
							style={achieved ? { borderColor: progressColor, color: progressColor } : {}}
						>
							<span className="milestone-icon">{achieved ? milestone.icon : '○'}</span>
							<span className="milestone-label">{milestone.label}</span>
						</div>
					);
				})}
			</div>
		</div>
	);
};
